import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Copy,
  CreditCard,
  Filter,
  Loader2,
  Search,
  XCircle
} from "lucide-react";

const BuilderConsultantSubscriptionHistory = ({ excludeLayout = false }) => {
  const navigate = useNavigate();
  const { token } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      const res = await axiosInstancev1.get("/builder/consultant-subscription/history", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data?.history || []);
    } catch (err) {
      console.error("Failed to load builder consultant subscription history:", err);
      toast.error(err.response?.data?.message || "Failed to load subscription history");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, type = "ID") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getStatusStyles = (status) => {
    const s = status?.toLowerCase();
    if (s === "active" || s === "success") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (s === "pending") return "bg-amber-100 text-amber-700 border-amber-200";
    if (s === "failed" || s === "cancelled") return "bg-rose-100 text-rose-700 border-rose-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const filteredHistory = useMemo(() => {
    return history.filter((item) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        item.merchant_order_id?.toLowerCase().includes(q) ||
        item.cashfree_order_id?.toLowerCase().includes(q);
      const matchesStatus = statusFilter === "all" || item.status?.toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [history, searchTerm, statusFilter]);

  const Content = (
    <div className={`flex flex-col ${!excludeLayout ? 'min-h-screen bg-gray-50' : ''}`}>
      {!excludeLayout && (
        /* Header */
        <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/builder/consultant-profile-subscription")}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Consultant Subscription History</h1>
                  <p className="text-sm text-gray-500">View and manage your past transactions</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                <CheckCircle size={16} className="text-blue-600" />
                <span className="font-semibold text-blue-800">{history.length}</span>
                <span>Total Transactions</span>
              </div>
            </div>

            {/* Search / Filters */}
            <div className="mt-6 flex flex-col md:flex-row gap-4">
              <div className="relative flex-1 group">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search by Order ID or Payment ID..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="relative min-w-[160px]">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <select
                  className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-blue-500/20 outline-none text-sm font-medium text-gray-700"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${excludeLayout ? 'py-4' : 'py-8'}`}>
        {excludeLayout && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4 w-full">
              <div className="relative flex-1 group">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search Transactions..."
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-xs"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="relative min-w-[140px]">
                <select
                  className="w-full pl-3 pr-8 py-2 bg-white border border-gray-200 rounded-xl outline-none text-xs font-medium text-gray-700"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <p className="mt-4 text-gray-500 font-medium">Loading transactions...</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-sm max-w-lg mx-auto">
            <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CreditCard className="text-gray-300 w-10 h-10" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">No transactions found</h2>
            <p className="text-gray-500 mt-2">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters to find what you're looking for."
                : "Subscribe to activate your consultant listing."}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <button
                onClick={() => navigate("/builder/consultant-profile-subscription")}
                className="mt-8 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
              >
                View Subscription
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Date</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Transaction IDs</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Amount</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Validity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredHistory.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900">{formatDate(item.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                            #{item.merchant_order_id}
                          </span>
                          <button
                            onClick={() => copyToClipboard(item.merchant_order_id, "Order ID")}
                            className="p-1 hover:text-blue-600 transition-all"
                            title="Copy Order ID"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                        {item.cashfree_order_id && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-gray-400 italic">Pay ID: {item.cashfree_order_id}</span>
                            <button
                              onClick={() => copyToClipboard(item.cashfree_order_id, "Payment ID")}
                              className="p-1 hover:text-blue-600 transition-all"
                              title="Copy Payment ID"
                            >
                              <Copy size={10} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm font-black text-gray-900 font-mono">
                        ₹{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusStyles(
                          item.status
                        )} uppercase tracking-tight`}
                      >
                        {item.status === "active" ? (
                          <CheckCircle size={10} className="mr-1" />
                        ) : item.status === "pending" ? (
                          <Clock size={10} className="mr-1" />
                        ) : (
                          <XCircle size={10} className="mr-1" />
                        )}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      {item.status === "active" && item.validity_end_date ? (
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-emerald-700">Ends {formatDate(item.validity_end_date)}</span>
                          <span className="text-[10px] text-gray-400">{item.duration_months} Months Plan</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (excludeLayout) return Content;

  return (
    <Layout>
      {Content}
    </Layout>
  );
};

export default BuilderConsultantSubscriptionHistory;

